import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { apiClient } from "../../../../api/client";
import { baseApi } from "../../../../services/rtkApi/baseApi";
import BasicButton from "../../../../components/basics/BasicButton";
import "../../styles/aotd.css";
import { url } from "../../../../api";
import { toast } from "react-toastify";
import emojiRegex from 'emoji-regex';
import GraphemeSplitter from "grapheme-splitter";
import { FaExclamationCircle } from "react-icons/fa";
import { useSetNextEmojisMutation } from "../../api/artOfTheDayApi";
import EmojiKeyboard from "../../../emojis/components/EmojiKeyboard";
import { emojiKey } from "../../../emojis/components/emojiCatalogShared";

interface NextAOTDProps {
    nextEmojis: string[];
}

interface UnlockOption {
    cardSlug: string;
    cardName: string;
    unlocksRemaining: number;
}

// improved emoji validation that allows multi-code-point sequences (e.g. 👨‍👩‍👧‍👦, 🏕️)
const splitter = new GraphemeSplitter();

const isEmojiCharacter = (str: string): boolean => {
    const cleaned = str.trim();

    // 1️⃣ split the string into extended-grapheme clusters
    const clusters = typeof (Intl as any).Segmenter !== "undefined"
        ? [...new (Intl as any).Segmenter(undefined, { granularity: "grapheme" }).segment(cleaned)].map((s: any) => s.segment)
        : splitter.splitGraphemes(cleaned);

    if (clusters.length !== 1) return false; // more than one visible symbol

    // 2️⃣ check that the single cluster is an emoji (fully-qualified sequences included)
    try {
        const regex = emojiRegex();
        return regex.test(clusters[0]);
    } catch (e) {
        console.error("emoji validation error:", e);
        return false;
    }
};

const NextAOTD: React.FC<NextAOTDProps> = ({ nextEmojis }) => {
    const [emojis, setEmojis] = useState(["", "", ""]);
    const [setNextEmojisMutation] = useSetNextEmojisMutation();
    const dispatch = useDispatch();
    const [inputError, setInputError] = useState([false, false, false]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [emojiSet, setEmojiSet] = useState(nextEmojis.length > 0);
    const [nnextEmojis, setnextEmojis] = useState(nextEmojis);
    const [errorDetails, setErrorDetails] = useState<string[]>([]);
    const [reusedIndexes, setReusedIndexes] = useState<number[]>([]);
    const [usedEmojis, setUsedEmojis] = useState<Array<{ emoji: string; date: Date }>>([]);
    const [unlockOption, setUnlockOption] = useState<UnlockOption | null>(null);
    const [unlockingEmoji, setUnlockingEmoji] = useState<string | null>(null);
    const [showPicker, setShowPicker] = useState(false);

    const refetchUsedEmojis = useCallback(async () => {
        try {
            const response = await apiClient.get(url + "/api/art-of-the-day/used-emojis");
            if (response.status === 200) {
                setUsedEmojis(response.data.usedEmojis || []);
                setUnlockOption(response.data.unlockOption || null);
            }
        } catch (error) {
            console.error("Error fetching used emojis:", error);
        }
    }, []);

    useEffect(() => {
        refetchUsedEmojis();
    }, [refetchUsedEmojis]);

    // keyed by emojiKey: the backend serves CANONICAL forms (ledger), while
    // typed input can be any byte variant (👍🏽, ❤ vs ❤️) — raw comparison
    // would miss the match and silently drop the red highlight/unlock prompt
    const usedEmojiMap = useMemo(() => {
        const m = new Map<string, Date>();
        for (const u of usedEmojis) {
            m.set(emojiKey(u.emoji), new Date(u.date));
        }
        return m;
    }, [usedEmojis]);

    // Distinct list of currently-typed reused emojis (for the unlock prompt).
    const reusedTypedEmojis = useMemo(() => {
        const seen = new Set<string>();
        const out: string[] = [];
        emojis.forEach((raw) => {
            const e = raw.trim();
            const k = emojiKey(raw);
            if (e && usedEmojiMap.has(k) && !seen.has(k)) {
                seen.add(k);
                out.push(e);
            }
        });
        return out;
    }, [emojis, usedEmojiMap]);

    useEffect(() => {
        const reused: Array<{ emoji: string; date: Date; index: number }> = [];

        emojis.forEach((raw, idx) => {
            const emoji = raw.trim();
            if (!emoji) return;
            const usedDate = usedEmojiMap.get(emojiKey(raw));
            if (usedDate) reused.push({ emoji, date: usedDate, index: idx });
        });

        setReusedIndexes(reused.map(r => r.index));
        setErrorDetails(
            reused.map(r => {
                const formattedDate = new Intl.DateTimeFormat("en-US", {
                    year: "numeric", month: "long", day: "numeric"
                }).format(r.date);
                return `${r.emoji} (used on ${formattedDate})`;
            })
        );

        if (reused.length > 0) {
            setErrorMessage("Some emojis have been used before. Please choose different ones.");
        } else {
            setErrorMessage(prev => prev?.toLowerCase().includes("used before") ? null : prev);
        }
    }, [emojis, usedEmojiMap]);

    const applyEmoji = (index: number, value: string, advanceFocus: boolean) => {
        // reject invalid input (non-emoji)
        if (value && !isEmojiCharacter(value)) {
            triggerInputError(index);
            return;
        }

        // each emoji can only appear once per submission (server rejects with
        // duplicate_emojis) — block the dupe at entry time instead
        if (value) {
            const k = emojiKey(value);
            const dupe = emojis.some((e, i) => i !== index && e.trim() && emojiKey(e) === k);
            if (dupe) {
                toast.info("You've already picked that emoji — each one can only be used once.");
                triggerInputError(index);
                return;
            }
        }

        updateEmojis(index, value);

        // only advance if valid, non-empty, AND not reused
        if (advanceFocus && value && !usedEmojiMap.has(emojiKey(value)) && index < emojis.length - 1) {
            focusNextInput(index, value);
        }
        // if reused: stay in current cell, useEffect will mark it red
    };

    // manual typing keeps the auto-advance behavior
    const handleEmojiChange = (index: number, value: string) => applyEmoji(index, value, true);

    // index of the slot input that currently holds focus, or -1
    const getFocusedSlotIndex = (): number => {
        const active = document.activeElement;
        if (active instanceof HTMLInputElement && active.id.startsWith("emoji-input-")) {
            const idx = Number(active.id.slice("emoji-input-".length));
            if (Number.isInteger(idx) && idx >= 0 && idx < emojis.length) return idx;
        }
        return -1;
    };

    // fill the first empty slot when the user picks from the emoji keyboard;
    // when all three are full, replace the focused slot instead (if any).
    // no focus-advance on a pick — the keyboard stays open (stayOpen) and
    // must not pull the OS keyboard back up mid multi-pick.
    const handleSelectFromGrid = (emoji: string) => {
        let idx = emojis.findIndex((e) => e.trim() === "");
        if (idx === -1) idx = getFocusedSlotIndex();
        if (idx === -1) {
            toast.info("All three emoji slots are full — clear one first.");
            return;
        }
        applyEmoji(idx, emoji, false);
    };

    // keyboard-swap UX: blur any focused slot input first so the OS keyboard
    // slides away and the emoji sheet takes its place (mobile), then toggle.
    const handleTogglePicker = () => {
        const active = document.activeElement;
        if (active instanceof HTMLElement && active.tagName === "INPUT") {
            active.blur();
        }
        setShowPicker((v) => !v);
    };

    const triggerInputError = (index: number) => {
        const newInputError = [...inputError];
        newInputError[index] = true;
        setInputError(newInputError);
        setTimeout(() => {
            newInputError[index] = false;
            setInputError(newInputError);
        }, 500);
    };

    const updateEmojis = (index: number, value: string) => {
        const newEmojis = [...emojis];
        newEmojis[index] = value;
        setEmojis(newEmojis);
    };

    const focusNextInput = (index: number, value: string) => {
        // wait for react to apply state update before moving focus
        if (value && index < emojis.length - 1) {
            setTimeout(() => {
                const nextInput = document.getElementById(`emoji-input-${index + 1}`);
                if (nextInput) {
                    (nextInput as HTMLInputElement).focus();
                }
            }, 0);
        }
    };

    const handleUnlockEmoji = useCallback(async (emoji: string) => {
        if (!unlockOption || unlockOption.unlocksRemaining <= 0) return;
        setUnlockingEmoji(emoji);
        try {
            const response = await apiClient.post(
                url + "/api/art-of-the-day/unlock-emoji",
                { emoji },
                {}
            );
            if (response.status === 200) {
                toast.success(`unlocked ${emoji}. ${response.data.unlocksRemaining} unlocks left.`);
                await refetchUsedEmojis();
                // the keyboard/selector caches per-user used flags — a reclaim
                // frees the emoji, so refresh them (this call bypasses RTK)
                dispatch(baseApi.util.invalidateTags(["EmojiSelector"]));
            }
        } catch (err: any) {
            const msg = err?.response?.data?.message || "Failed to unlock emoji.";
            toast.error(msg);
        } finally {
            setUnlockingEmoji(null);
        }
    }, [unlockOption, refetchUsedEmojis, dispatch]);

    const handleSubmit = async () => {
        // Filter out empty fields
        const filteredEmojis = emojis.filter(emoji => emoji.trim() !== "");

        // Require at least one emoji
        if (filteredEmojis.length === 0) {
            setErrorMessage("Please enter at least one emoji.");
            return;
        }

        if (!areAllEmojisValid(filteredEmojis)) {
            setErrorMessage("Please enter valid emoji characters only.");
            return;
        }

        await submitEmojis(filteredEmojis);
    };

    // check if at least one field has an emoji
    const hasAtLeastOneEmoji = () => {
        return emojis.some(emoji => emoji.trim() !== "");
    };

    const areAllEmojisValid = (emojis: string[]) => {
        return emojis.every((emoji) => isEmojiCharacter(emoji));
    };

    const submitEmojis = async (filteredEmojis: string[]) => {
        try {
            await setNextEmojisMutation({ emojis: filteredEmojis }).unwrap();
            toast.success("Emojis updated successfully.");
            setErrorMessage(null);
            setEmojiSet(true);
            setnextEmojis(filteredEmojis);
        } catch (err: any) {
            handleSubmissionError(err);
        }
    };

    // `err` here is RTK Query's FetchBaseQueryError shape:
    //   { status: number, data: { error?, message?, reusedEmojis?, unlockOption? } }
    const handleSubmissionError = (err: any) => {
        const data = err?.data;
        if (err?.status === 400 && data?.reusedEmojis) {
            const reusedEmojiArr: { emoji: string; date: string }[] = data.reusedEmojis;

            // The 400 response carries the unlockOption inline so the
            // prompt stays in sync if the user just played the card.
            if (data.unlockOption) {
                setUnlockOption(data.unlockOption);
            }

            // format list for display
            const formatted = reusedEmojiArr.map((e) => {
                const formattedDate = new Intl.DateTimeFormat("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                }).format(new Date(e.date));
                return `${e.emoji} (used on ${formattedDate})`;
            });

            setErrorDetails(formatted);

            const reusedIdx = emojis
                .map((em, idx) =>
                    reusedEmojiArr.some((r) => emojiKey(r.emoji) === emojiKey(em)) ? idx : -1,
                )
                .filter((i) => i !== -1);
            setReusedIndexes(reusedIdx);

            setErrorMessage("Some emojis have been used before. please choose different ones.");
            toast.error("Some emojis have been used in the past. Please choose different emojis.");
        } else if (err?.status === 400 && data?.error === "duplicate_emojis") {
            // mark every slot that shares a key with an earlier slot
            const seen = new Set<string>();
            const dupIdx: number[] = [];
            emojis.forEach((em, idx) => {
                if (!em.trim()) return;
                const k = emojiKey(em);
                if (seen.has(k)) dupIdx.push(idx);
                else seen.add(k);
            });
            setReusedIndexes(dupIdx);
            setErrorMessage("Each emoji can only appear once in your selection.");
            toast.error("Each emoji can only appear once in your selection.");
        } else if (data?.message) {
            setErrorMessage(data.message);
            toast.error(data.message);
        } else {
            console.error("Error updating emojis:", err);
            toast.error("An error occurred while updating the emojis.");
        }
    };

    // Disable submit button if no emoji is entered
    const isSubmitDisabled = !hasAtLeastOneEmoji();

    return emojiSet ? (
        // <DisplayEmojis nnextEmojis={nnextEmojis} />
        null
    ) : (
        <div className="flex flex-col items-center">
            <EmojiInputForm
                emojis={emojis}
                inputError={inputError}
                handleEmojiChange={handleEmojiChange}
                handleSubmit={handleSubmit}
                isSubmitDisabled={isSubmitDisabled}
                errorMessage={errorMessage}
                errorDetails={errorDetails}
                reusedIndexes={reusedIndexes}
                unlockOption={unlockOption}
                reusedTypedEmojis={reusedTypedEmojis}
                unlockingEmoji={unlockingEmoji}
                onUnlockEmoji={handleUnlockEmoji}
            />
            {/* platform emoji keyboard — bottom sheet on mobile, dropdown on
                desktop (the relative wrapper anchors the dropdown under the
                trigger). stayOpen lets the user fill all three slots in one
                go; it closes via its backdrop/sheet-dismiss or the trigger. */}
            <div className="relative w-full max-w-md mx-auto mb-2 px-2">
                <button
                    type="button"
                    onClick={handleTogglePicker}
                    className="text-sm text-indigo-600 underline hover:text-indigo-800"
                >
                    {showPicker ? "Hide the registry ▲" : "Pick from the registry ▾"}
                </button>
                <EmojiKeyboard
                    open={showPicker}
                    onClose={() => setShowPicker(false)}
                    onPick={handleSelectFromGrid}
                    mode="aotd"
                    stayOpen
                    allowRevive={!!(unlockOption && unlockOption.unlocksRemaining > 0)}
                    title="Pick your next AOTD emojis"
                />
            </div>
        </div>
    );
};

const DisplayEmojis: React.FC<{ nnextEmojis: string[] }> = ({ nnextEmojis }) => (
    <div className="flex flex-row items-center max-w-md mx-auto">
        <p className="text-gray-800 text-base m-4">
            <span className="font-bold ">next AOTD emojis: </span>
        </p>
        <div className="flex gap-2">
            {nnextEmojis.map((emoji, index) => emoji && (
                <span key={index} className="text-2xl">
                    {emoji}
                </span>
            ))}
        </div>
    </div>
);

const EmojiInputForm: React.FC<{
    emojis: string[];
    inputError: boolean[];
    handleEmojiChange: (index: number, value: string) => void;
    handleSubmit: () => void;
    isSubmitDisabled: boolean;
    errorMessage: string | null;
    errorDetails: string[];
    reusedIndexes: number[];
    unlockOption: UnlockOption | null;
    reusedTypedEmojis: string[];
    unlockingEmoji: string | null;
    onUnlockEmoji: (emoji: string) => void;
}> = ({ emojis, inputError, handleEmojiChange, handleSubmit, isSubmitDisabled, errorMessage, errorDetails, reusedIndexes, unlockOption, reusedTypedEmojis, unlockingEmoji, onUnlockEmoji }) => (
    <div className="flex flex-col m-2 border-4 border-gray-500 shadow-indigo-400 rounded-sm shadow items-center p-5 max-w-md mx-auto">
        <p className="text-gray-800 text-base m-2">
            <span className="font-bold text-indigo-500">your next AOTD's emojis </span>
        </p>
        <small className="text-gray-400 mb-2 text-justify">
            these emojis will be used to create the next AOTD. please enter at least one emoji. mind that you cannot reuse the same emoji ever again.
        </small>
        <div className="flex gap-2">
            {emojis.map((emoji, index) => (
                <input
                    id={`emoji-input-${index}`}
                    key={index}
                    type="text"
                    value={emoji}
                    onChange={(e) => handleEmojiChange(index, e.target.value)}
                    placeholder="-"
                    className={`w-12 p-2 rounded border-2 text-center text-xl focus:outline-none
            ${inputError[index]
                            ? "border-red-500 focus:border-red-600 border-spacing-4 animate-wiggle"
                            : "border-gray-300 focus:border-blue-500"
                        } ${reusedIndexes.includes(index) ? "border-red-500 focus:border-red-600 placeholder-red-500" : "placeholder-gray-400"}`}
                />
            ))}
        </div>
        <BasicButton
            text="Set Emojis"
            onClick={handleSubmit}
            bgColor="bg-blue-500"
            hoverBgColor="hover:bg-blue-600"
            textColor="text-white"
            className="mt-4"
            disabled={isSubmitDisabled}
        />
        {/* Reclamation prompt — only shows when the user has played the card
            AND has typed a previously-used emoji. One Unlock button per
            distinct reused emoji currently in the inputs. */}
        {unlockOption && unlockOption.unlocksRemaining > 0 && reusedTypedEmojis.length > 0 && (
            <div className="flex flex-col items-start w-full mt-3 rounded border border-indigo-300 bg-indigo-50 p-3 text-sm text-indigo-800">
                <div className="flex items-center mb-2">
                    <span className="font-bold mr-1">♻️ {unlockOption.cardName}</span>
                    <span className="text-indigo-600">— {unlockOption.unlocksRemaining} unlock{unlockOption.unlocksRemaining === 1 ? "" : "s"} left</span>
                </div>
                <div className="flex flex-col gap-2 w-full">
                    {reusedTypedEmojis.map((e) => (
                        <div key={e} className="flex items-center justify-between gap-2">
                            <span>wanna unlock <span className="text-xl">{e}</span>?</span>
                            <BasicButton
                                text={unlockingEmoji === e ? "unlocking…" : `unlock ${e}`}
                                onClick={() => onUnlockEmoji(e)}
                                bgColor="bg-indigo-500"
                                hoverBgColor="hover:bg-indigo-600"
                                textColor="text-white"
                                disabled={unlockingEmoji !== null}
                            />
                        </div>
                    ))}
                </div>
            </div>
        )}
        {/* enhanced error message */}
        {errorMessage && (
            <div className="flex flex-col items-start w-full mt-3 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                <div className="flex items-start">
                    <FaExclamationCircle className="flex-shrink-0 h-5 w-5 text-red-600 mr-2" />
                    <span>{errorMessage}</span>
                </div>
                {errorDetails.length > 0 && (
                    <ul className="list-disc list-inside mt-2">
                        {errorDetails.map((detail, idx) => (
                            <li key={idx}>{detail}</li>
                        ))}
                    </ul>
                )}
            </div>
        )}
    </div>
);

export default NextAOTD;
